<template>
  <div class="w-full h-full flex flex-col">
    <div class="w-full flex-grow">
      <div
        v-if="isEditing"
        class="grid-cols-1 border rounded w-full h-full"
      >
        <div class="flex flex-wrap gap-2 p-2">
          <HeadingControls v-model:editor="editor" />
          <BoldControls v-model:editor="editor" />
          <ItalicControls v-model:editor="editor" />
          <UnderlineControls v-model:editor="editor" />
          <HighlightControls v-model:editor="editor" />
          <TextAlignControls v-model:editor="editor" />
          <BulletAndOrderListControls v-model:editor="editor" />
          <LinkControls v-model:editor="editor" />
          <TableControls
            v-model:editor="editor"
            :borderRight="borderRight"
          />
          <ImageControls
            v-model:editor="editor"
            :businessId="props.businessId"
          />
          <ColorControls v-model:editor="editor" />
          <IndentControls v-model:editor="editor" />
          <AIControls
            v-model:editor="editor"
            v-model:data="data"
          />
          <SpeechToText
            v-model:editor="editor"
            v-model:data="data"
          />
          <slot name="controls" />
        </div>
        <div class="border-t p-2 w-full flex-grow whitespace-pre-line max-h-[400px] overflow-y-auto">
          <editor-content
            :editor="editor"
            class="outline-none h-full whitespace-pre-line"
          />
        </div>
      </div>
      <section
        v-else
        class="h-full"
      >
        <!-- <div v /> -->

        <!-- <editor-content :editor="editor" class=" w-full px-4 py-2 " /> -->
        <editor-content
          :editor="editor"
          class="w-full h-full"
          :disabled="true"
          translate="yes"
        />
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Editor, EditorContent } from '@tiptap/vue-3'
import { onBeforeUnmount, onMounted, ref } from 'vue'

// Extensions
import { Color } from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import Link from '@tiptap/extension-link'
import Table from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import TextAlign from '@tiptap/extension-text-align'
import TextStyle from '@tiptap/extension-text-style'
import Underline from '@tiptap/extension-underline'
import StarterKit from '@tiptap/starter-kit'
import ImageResize from 'tiptap-extension-resize-image'
import Indent from './extensions/Indent'
import SpeechToText from './SpeechToText.vue'

// Controls
import AIControls from './AIGenerate'
import BoldControls from './Bold.vue'
import BulletAndOrderListControls from './BulletAndOrderList'
import ColorControls from './Color.vue'
import HeadingControls from './Heading.vue'
import HighlightControls from './Highlight'
import ImageControls from './Image'
import IndentControls from './Indent.vue'
import ItalicControls from './Italic.vue'
import LinkControls from './Link'
import TableControls from './Table'
import TextAlignControls from './TextAlign'
import UnderlineControls from './Underline.vue'

const props = defineProps(['businessId'])

const data = defineModel<string>('data', {
  required: true,
  default: () => '<p>   </p>',
})

const isEditing = defineModel<boolean>('isEditing', {
  required: true,
  default: true,
})

const emit = defineEmits<{
  (e: 'update', data: string): void
}>()

const editor = ref<Editor | null>(null)

onMounted(() => {
  editor.value = new Editor({
    editable: isEditing.value,
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
        translate: 'yes',
      },
    },
    onUpdate: ({ editor }) => {
      const content = editor.getHTML()
      data.value = content
      emit('update', content)
    },
    extensions: [
      StarterKit,
      Underline,
      Highlight.configure({
        multicolor: true,
      }),
      Link.configure({
        openOnClick: false, // Prevent links from opening when clicked in the editor
        HTMLAttributes: {
          class: 'editor-link',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      ImageResize.configure({
        inline: false,
        allowBase64: true,
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TextStyle,
      Color,
      Indent,
    ],
    content: data.value,
  })
})

watch(
  () => data.value,
  async (newData, oldData) => {
    //  check if same
    if (
      JSON.stringify(newData) !== JSON.stringify(editor.value.getHTML())
    ) {
      editor.value.commands.setContent(newData)
    }
  },
)

watch(
  () => editor.value?.getHTML(),
  async (newData, oldData) => {
    //  check if same
    data.value = newData
    emit('update', newData)
  },
)

watch(
  () => editor.value?.isEditable,
  (newData, oldData) => {
  },
)

defineExpose({ editor })

const borderRight = computed(() => {
  return isEditing.value ? '1px solid #817f7f' : 'none'
})

onBeforeUnmount(() => {
  editor.value?.destroy()
})
</script>

<style>
.ProseMirror:focus {
  outline: none;

}
.tiptap table {
  border-collapse: collapse;
  table-layout: fixed;
  width: auto;
  margin: 0;
  overflow: hidden;
}
.tiptap td,
.tiptap th {
  min-width: 1em;
  border-bottom: 1px solid #817f7f;
  border-right: v-bind(borderRight);
  padding: 3px 5px;
  vertical-align: top;
  box-sizing: border-box;
  position: relative;
}

.tiptap table colgroup col:nth-child(1),
.tiptap table colgroup col:nth-child(2) {
  min-width: 200px !important;
}
</style>
